import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, qs } from "@/lib/api";
import { KpiCard, SectionTitle } from "@/components/Kpi";
import { COLORS, ComparisonChart } from "@/components/Charts";
import { DettaglioGiorno } from "@/components/DettaglioGiorno";
import { FilterBar, useFiltersMeta, MESE_CORRENTE } from "@/components/Filters";
import { MESI } from "@/lib/format";
import { TempoRisposta } from "@/components/TempoRisposta";
import { useAuth } from "@/context/AuthContext";

const ANNO = 2026;

/** Pratiche giornaliere di un singolo commerciale: KPI, tempo di risposta e istogrammi per giorno. */
export default function CommercialeGiornaliero() {
  const { slug } = useParams();
  const { user, puo } = useAuth();
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: ANNO, mese: MESE_CORRENTE, commerciale: slug });
  const [d, setD] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [nome, setNome] = useState(slug);
  const [dettaglio, setDettaglio] = useState(null);
  const mese = filters.mese;
  const apri = (tipo, chart) => (p) => p?.data && setDettaglio((cur) =>
    cur && cur.data === p.data && cur.chart === chart ? null : { data: p.data, tipo, chart });
  const pannello = (chart) => dettaglio?.chart === chart && !annoIntero && (
    <DettaglioGiorno data={dettaglio.data} tipo={dettaglio.tipo} filters={filters} onClose={() => setDettaglio(null)} />
  );
  useEffect(() => { setDettaglio(null); }, [filters]);

  useEffect(() => {
    api.get("/commerciali").then((r) => {
      const c = (r.data.commerciali || []).find((x) => x.slug === slug);
      if (c) setNome(c.nome || slug);
    }).catch(() => {});
  }, [slug]);
  useEffect(() => {
    setFilters((f) => ({ ...f, commerciale: slug }));
  }, [slug]);
  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ ...filters, dealer: "" })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.mese, filters.macro, filters.prodotto, filters.commerciale]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get(`/dashboard/giornaliero?${qs(filters)}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [filters]);

  const annoIntero = !mese;
  const giorni = d?.giorni || [];
  const serie = annoIntero
    ? (d?.serie_mensile || []).map((r) => ({ label: r.label, caricate: r.pratiche_2026, importo_caricato: r.caricato_2026,
      liquidate: r.liquidate_2026, importo_liquidato: r.liquidato_2026 }))
    : giorni;
  const serieLiq = annoIntero ? serie : (d?.liquidate_giorni || []);
  const t = d?.totali || {};
  const oggiIso = new Date().toLocaleDateString("sv-SE");
  const praticheOggi = giorni.find((g) => g.data === oggiIso)?.caricate ?? 0;
  const periodo = annoIntero ? `${ANNO}` : `${MESI[mese - 1]} ${ANNO}`;

  return (
    <div data-testid="commerciale-giornaliero-page">
      <SectionTitle sub={`Pratiche caricate giorno per giorno · ${periodo}`}
        right={<div className="flex items-center gap-3">
          <Link to={`/commerciali/${slug}`} data-testid="cg-torna-commerciale"
            className="text-sm text-sky-700 hover:text-sky-900 transition-colors duration-200">← Scheda commerciale</Link>
        </div>}>
        Pratiche giornaliere · {nome}
      </SectionTitle>
      <FilterBar meta={meta} value={filters} onChange={setFilters}
        fields={["mese", "macro", "prodotto", "dealer"]} dealers={dealers}
        extra={
          <button type="button" data-testid="cg-filtri-reset"
            onClick={() => setFilters({ anno: ANNO, mese: MESE_CORRENTE, commerciale: slug })}
            className="text-sm px-4 py-2 rounded-md bg-sky-600 text-white font-semibold
              hover:bg-sky-700 transition-colors duration-200">
            Azzera filtri
          </button>
        } />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="cg-kpi-oggi" label="Pratiche odierne" value={praticheOggi} accent="caricato" />
        <KpiCard testId="cg-kpi-caricate" label="Pratiche caricate" value={t.pratiche_caricate} accent="caricato" />
        <KpiCard testId="cg-kpi-importo" label="Importo caricato" value={t.importo_caricato} kind="eur" accent="caricato" />
        <KpiCard testId="cg-kpi-media" label="Media giornaliera"
          value={d?.giorni_attivi ? Math.round((t.pratiche_caricate || 0) / d.giorni_attivi) : null} accent="caricato" />
      </div>

      {(puo("tempo_risposta") || user?.role === "MASTER") && (
        <div className="mb-6"><TempoRisposta filters={filters} puoScaricare={user?.role === "MASTER"} /></div>
      )}

      <div className="space-y-6">
        <ComparisonChart testId="cg-chart-caricate" height={320} labelFormat="int"
          title={annoIntero ? "Pratiche caricate per mese" : "Pratiche caricate per giorno · clicca un giorno per il dettaglio"} data={serie}
          onBarClick={annoIntero ? undefined : apri("caricato", "caricate")}
          keys={[{ key: "caricate", name: "Caricate", color: COLORS.caricato }]} emptyNote="nessuna pratica nel periodo" />
        {pannello("caricate")}
        <ComparisonChart testId="cg-chart-importo" height={320}
          title={annoIntero ? "Importo caricato per mese" : "Importo caricato per giorno · clicca un giorno per il dettaglio"} data={serie}
          onBarClick={annoIntero ? undefined : apri("caricato", "importo")}
          keys={[{ key: "importo_caricato", name: "Caricato", color: COLORS.caricato }]} emptyNote="nessun importo nel periodo" />
        {pannello("importo")}
        <ComparisonChart testId="cg-chart-ingressi" height={300} type="line" labelFormat="int"
          title={annoIntero ? "Ingresso pratiche per mese · da DATA INGRESSO" : "Ingresso pratiche per giorno · da DATA INGRESSO"}
          data={d?.ingressi || []} keys={[{ key: "ingressi", name: "Ingressi", color: "#0284C7" }]}
          emptyNote="nessun ingresso nel periodo" />
        <ComparisonChart testId="cg-chart-liquidate" height={300} labelFormat="int"
          title={annoIntero ? "Pratiche liquidate per mese" : "Pratiche liquidate per giorno · clicca un giorno per il dettaglio"} data={serieLiq}
          onBarClick={annoIntero ? undefined : apri("liquidato", "liquidate")}
          keys={[{ key: "liquidate", name: "Liquidate", color: COLORS.liquidato }]} emptyNote="nessuna liquidazione nel periodo" />
        {pannello("liquidate")}
        <ComparisonChart testId="cg-chart-liquidato" height={300}
          title={annoIntero ? "Importo liquidato per mese" : "Importo liquidato per giorno · clicca un giorno per il dettaglio"} data={serieLiq}
          onBarClick={annoIntero ? undefined : apri("liquidato", "liquidato")}
          keys={[{ key: "importo_liquidato", name: "Liquidato", color: COLORS.liquidato }]} emptyNote="nessun importo liquidato nel periodo" />
        {pannello("liquidato")}
      </div>
    </div>
  );
}
